import {
  ContextWithStringAmount,
  GeneralErrorEvent,
  GetStoredTxResponseEvent,
  XStateSchema,
} from 'common/models/xstate';
import { ChainId } from 'constants/constants';
import { IchiSwapApi } from 'sdk/ichiSwapApi';
import { InputUtils } from 'utils/inputUtils';
import { StorageUtils } from 'utils/storageUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, Machine, interpret, spawn } from 'xstate';
import {
  IchiSwapEvent,
  IchiSwapEvents,
  EventUpdateAccountInfo,
  EventChangeAmount,
} from './ichiSwapEvents';
import { createTxMachine, initialTxContext, RemoteTxEvent } from './txMachine';
import { XStateUtils } from './xStateUtils';
import { Nullable } from 'common/models/optional';
import { TokenUtils } from 'utils/tokenUtils';
import { Address } from 'viem';

enum IchiSwapService {
  APPROVE = 'APPROVE',
  RESET_UPGRADE = 'RESET_UPGRADE',
  RESET_ALLOWANCE = 'RESET_ALLOWANCE',
  GET_UPGRADE_STORED_TX = 'GET_UPGRADE_STORED_TX',
  UPGRADE = 'UPGRADE',
  GET_DATA = 'GET_DATA',
}

type IchiSwapContext = {
  amount: string;
  amountToConvert: number;
  isSwapping: boolean;
  isApproving: boolean;
  isLoadingData: boolean;
  isIchiSwappingDisabled: boolean;
  isResettingAllowance: boolean;
  error: any;
  storedTx: Nullable<StorageUtils.StoredTx>;
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  inputError?: string;
  dataResponse: Nullable<IchiSwapApi.DataResponse>;
  dataError: Nullable<Error>;
  txRef: any;
} & ContextWithStringAmount;

export const initialIchiSwapContext: IchiSwapContext = {
  amount: '0',
  amountToConvert: 0,
  isSwapping: false,
  isApproving: false,
  isLoadingData: false,
  isIchiSwappingDisabled: false,
  isResettingAllowance: false,
  error: null,
  storedTx: null,
  accountAddress: null,
  chainId: null,
  dataResponse: null,
  dataError: null,
  txRef: null,
};

enum IchiSwapState {
  BASE_STATE = 'BASE_STATE',
  LOADING_DATA = 'LOADING_DATA',
  CHECK_INPUT = 'CHECK_INPUT',

  CHECKING_FOR_EXISTING_UPGRADE_TX = 'CHECKING_FOR_EXISTING_UPGRADE_TX',
  UPGRADE_ICHI_AND_GET_TX = 'UPGRADE_ICHI_AND_GET_TX',
  HANDLE_UPGRADE_ICHI_TX = 'HANDLE_UPGRADE_ICHI_TX',

  APPROVE_AND_GET_TX = 'APPROVE_AND_GET_TX',
  HANDLE_APPROVE_TX = 'HANDLE_APPROVE_TX',

  CHECKING_FOR_EXISTING_RESET_TX = 'CHECKING_FOR_EXISTING_RESET_TX',
  RESET_ALLOWANCE_AND_GET_TX = 'RESET_ALLOWANCE_AND_GET_TX',
  HANDLE_RESET_TX = 'HANDLE_RESET_TX',

  // Error States
  INVALID_INPUT = 'INVALID_INPUT',
  INPUT_TOO_SMALL = 'INPUT_TOO_SMALL',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
}

const ichiSwapApi: IchiSwapApi.IIchiSwap = IchiSwapApi.IchiSwap.getInstance();

type GetDataResponseEvent = {
  type: string;
  data: IchiSwapApi.DataResponse;
};

const ichiSwapMachine = Machine<
  IchiSwapContext,
  XStateSchema<IchiSwapState>,
  IchiSwapEvents
>(
  {
    key: 'ichiSwap',
    initial: IchiSwapState.BASE_STATE,
    context: XStateUtils.shortStringify(initialIchiSwapContext),
    // Interesting approach here with the bridgeMachine, I wonder if this solves a problem
    // of being able to call events not in a specific state, unclear
    // on: {
    // },
    states: {
      [IchiSwapState.BASE_STATE]: {
        on: {
          [IchiSwapEvent.UPGRADE_ICHI]: {
            target: IchiSwapState.UPGRADE_ICHI_AND_GET_TX,
          },
          [IchiSwapEvent.APPROVE]: {
            target: IchiSwapState.APPROVE_AND_GET_TX,
          },
          [IchiSwapEvent.UPDATE_ACCOUNT_INFO]: {
            target: IchiSwapState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
          [IchiSwapEvent.INPUT_CHANGE]: {
            target: IchiSwapState.BASE_STATE,
            actions: assign({
              amount: (_, event: EventChangeAmount) => event.value.amount,
            }),
          },
          [IchiSwapEvent.RESET_ALLOWANCE]: {
            target: IchiSwapState.RESET_ALLOWANCE_AND_GET_TX,
          },
        },
      },
      // -----------------------------------------------------------------------------
      // Handle approval
      // -----------------------------------------------------------------------------
      [IchiSwapState.APPROVE_AND_GET_TX]: {
        entry: assign({
          isApproving: () => true,
        }),
        exit: assign({
          isApproving: () => false,
        }),
        invoke: [
          {
            src: IchiSwapService.APPROVE,
            onDone: {
              target: IchiSwapState.HANDLE_APPROVE_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: IchiSwapState.CHECK_INPUT,
            },
          },
        ],
      },
      [IchiSwapState.HANDLE_APPROVE_TX]: {
        entry: assign({
          isApproving: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as IchiSwapContext).chainId,
                accountAddress: (context as unknown as IchiSwapContext)
                  .accountAddress,
                storedTx: (context as unknown as IchiSwapContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as IchiSwapContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isApproving: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: IchiSwapState.LOADING_DATA,
          },
        },
      },
      // -----------------------------------------------------------------------------
      // Handle upgrading from legacy to v2 ICHI
      // -----------------------------------------------------------------------------
      [IchiSwapState.CHECKING_FOR_EXISTING_UPGRADE_TX]: {
        invoke: [
          {
            src: IchiSwapService.GET_UPGRADE_STORED_TX,
            // Success means there is a mint tx, re-execute mint
            onDone: {
              target: IchiSwapState.HANDLE_UPGRADE_ICHI_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            // Error just means no tx found, go back to waiting for input
            onError: {
              target: IchiSwapState.CHECK_INPUT,
            },
          },
        ],
      },
      [IchiSwapState.UPGRADE_ICHI_AND_GET_TX]: {
        entry: assign({
          isSwapping: () => true,
        }),
        exit: assign({
          isSwapping: () => false,
        }),
        invoke: [
          {
            src: IchiSwapService.UPGRADE,
            onDone: {
              target: IchiSwapState.CHECKING_FOR_EXISTING_UPGRADE_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: IchiSwapState.CHECK_INPUT,
              actions: assign({
                error: (_context, event) => event.data,
              }),
            },
          },
        ],
      },
      [IchiSwapState.HANDLE_UPGRADE_ICHI_TX]: {
        entry: assign({
          isSwapping: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as IchiSwapContext).chainId,
                accountAddress: (context as unknown as IchiSwapContext)
                  .accountAddress,
                storedTx: (context as unknown as IchiSwapContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as IchiSwapContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isSwapping: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [
              RemoteTxEvent.REMOTE_TX_SUCCESS,
              assign({
                amount: () => '0',
              }),
            ],
            target: IchiSwapState.LOADING_DATA,
          },
        },
      },
      // -----------------------------------------------------------------------------
      // Handle reset
      // -----------------------------------------------------------------------------
      [IchiSwapState.RESET_ALLOWANCE_AND_GET_TX]: {
        entry: assign({
          isResettingAllowance: () => true,
        }),
        exit: assign({
          isResettingAllowance: () => false,
        }),
        invoke: [
          {
            src: IchiSwapService.RESET_ALLOWANCE,
            onDone: {
              target: IchiSwapState.HANDLE_RESET_TX,
              actions: assign({
                storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
              }),
            },
            onError: {
              target: IchiSwapState.BASE_STATE,
              actions: assign({
                error: (_context, event) => event.data,
              }),
            },
          },
        ],
      },
      [IchiSwapState.HANDLE_RESET_TX]: {
        entry: assign({
          isResettingAllowance: () => true,
          txRef: (context) =>
            spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: (context as unknown as IchiSwapContext).chainId,
                accountAddress: (context as unknown as IchiSwapContext)
                  .accountAddress,
                storedTx: (context as unknown as IchiSwapContext).storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as IchiSwapContext).storedTx?.hash
                }`,
              }
            ),
        }),
        exit: assign({
          isResettingAllowance: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: IchiSwapState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: IchiSwapState.LOADING_DATA,
          },
        },
      },
      [IchiSwapState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: [
          {
            src: IchiSwapService.GET_DATA,
            onDone: {
              target: IchiSwapState.CHECKING_FOR_EXISTING_UPGRADE_TX,
              actions: assign({
                dataResponse: (_, event: GetDataResponseEvent) => event.data,
              }),
            },
            onError: {
              target: IchiSwapState.CHECK_INPUT,
              actions: assign({
                dataError: (_, event: GeneralErrorEvent) => event.data,
              }),
            },
          },
        ],
      },
      [IchiSwapState.CHECK_INPUT]: {
        // This exit here is a great way to set context when the state itself is exiting so whatever is assigned
        // has access to the latest context.
        invoke: [],
        exit: assign({
          isIchiSwappingDisabled: (context) => {
            return (
              context.isSwapping ||
              // context.isLoadingData ||
              InputUtils.isDefaultInput(context) ||
              InputUtils.isZeroInput(context) ||
              InputUtils.isInputTooSmall(context) ||
              InputUtils.isInvalidInput(context) ||
              InputUtils.isNullChainId(context) ||
              InputUtils.isTooManyDecimals18(context) ||
              context.inputError != null
            );
          },
        }),
        always: [
          {
            target: IchiSwapState.BASE_STATE,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: IchiSwapState.BASE_STATE,
            cond: InputUtils.isZeroInput,
          },
          {
            target: IchiSwapState.NO_WALLET_CONNECTED,
            cond: InputUtils.isNotConnected,
          },
          {
            target: IchiSwapState.INPUT_TOO_SMALL,
            cond: InputUtils.isInputTooSmall,
          },
          {
            target: IchiSwapState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals18,
          },
          // {
          //   target: IchiSwapState.INSUFFICIENT_COLLATERAL,
          //   cond: isInsufficientCollateral
          // },
          // {
          //   target: IchiSwapState.INSUFFICIENT_MEMBER,
          //   cond: isInsufficientMember
          // },
          {
            target: IchiSwapState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: IchiSwapState.BASE_STATE,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [IchiSwapState.NO_WALLET_CONNECTED]: {
        entry: assign({
          inputError: () => 'Please connect a wallet to Mint!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [IchiSwapEvent.INPUT_CHANGE]: {
            target: IchiSwapState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [IchiSwapEvent.UPDATE_ACCOUNT_INFO]: {
            target: IchiSwapState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [IchiSwapState.INVALID_INPUT]: {
        entry: assign({
          inputError: () => 'Invalid Input!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [IchiSwapEvent.INPUT_CHANGE]: {
            target: IchiSwapState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [IchiSwapEvent.UPDATE_ACCOUNT_INFO]: {
            target: IchiSwapState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [IchiSwapState.INPUT_TOO_SMALL]: {
        entry: assign({
          inputError: () => 'Amount is too small!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [IchiSwapEvent.INPUT_CHANGE]: {
            target: IchiSwapState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [IchiSwapEvent.UPDATE_ACCOUNT_INFO]: {
            target: IchiSwapState.CHECK_INPUT,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
      [IchiSwapState.TOO_MANY_DECIMALS]: {
        entry: assign({
          inputError: () => 'Too many decimals!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [IchiSwapEvent.INPUT_CHANGE]: {
            target: IchiSwapState.LOADING_DATA,
            actions: assign({
              amount: (_, event) => event.value.amount,
            }),
          },
          [IchiSwapEvent.UPDATE_ACCOUNT_INFO]: {
            target: IchiSwapState.BASE_STATE,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
        },
      },
    },
  },
  {
    services: {
      [IchiSwapService.GET_DATA]: async (context) => {
        const contracts = TokenUtils.lookupIchiContracts(
          context.chainId as ChainId
        );
        return ichiSwapApi.getData({
          amount: context.amount,
          chainId: context.chainId as ChainId,
          accountAddress: context.accountAddress as Address,
          legacyIchiContainer: contracts?.legacyIchiContainer,
          ichiV2Container: contracts?.ichiV2Container,
        });
      },
      [IchiSwapService.APPROVE]: async (context) => {
        const contracts = TokenUtils.lookupIchiContracts(
          context.chainId as ChainId
        );
        return ichiSwapApi.approveConversion({
          chainId: context.chainId as ChainId,
          accountAddress: context.accountAddress as Address,
          legacyIchiContainer: contracts?.legacyIchiContainer,
          ichiV2Container: contracts?.ichiV2Container,
        });
      },
      [IchiSwapService.GET_UPGRADE_STORED_TX]: async (context) => {
        return TransactionUtils.getStoredTx({
          accountAddress: context.accountAddress as string,
          chainId: context.chainId as number,
          functionName: IchiSwapApi.IchiSwap.convertIchiToIchiV2FN,
        });
      },
      [IchiSwapService.UPGRADE]: async (context) => {
        const contracts = TokenUtils.lookupIchiContracts(
          context.chainId as ChainId
        );
        return ichiSwapApi.convertIchiToIchiV2({
          amount: context.amount,
          chainId: context.chainId as ChainId,
          accountAddress: context.accountAddress as Address,
          legacyIchiContainer: contracts?.legacyIchiContainer,
          ichiV2Container: contracts?.ichiV2Container,
        });
      },
      [IchiSwapService.RESET_ALLOWANCE]: async (context) => {
        const contracts = TokenUtils.lookupIchiContracts(
          context.chainId as ChainId
        );
        return ichiSwapApi.resetAllowance({
          chainId: context.chainId as ChainId,
          accountAddress: context.accountAddress as Address,
          legacyIchiContainer: contracts?.legacyIchiContainer,
          ichiV2Container: contracts?.ichiV2Container,
        });
      },
    },
  }
);

export const ichiSwapService = interpret(ichiSwapMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

ichiSwapService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[ichiSwapService:debug]', s);
  }
});
